import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, Mail, Lock, User, ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import { CheckInbox } from '../components/CheckInbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const res = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        login(res.data.user, res.data.token);
        toast.success('Welcome back!');
        
        // Redirect based on role and business
        if (res.data.user.role === 'admin') {
          navigate('/admin');
        } else if (res.data.user.role === 'sales_rep') {
          navigate('/sales');
        } else if (res.data.user.business_id) {
          navigate(`/business/${res.data.user.business_id}`);
        } else {
          navigate('/pricing');
        }
      } else {
        // Register
        if (!formData.name) {
          toast.error('Name is required');
          setLoading(false);
          return;
        }
        
        const res = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone
        });
        
        if (res.data.verification_required) {
          setPendingEmail(res.data.email);
          return;
        }
        login(res.data.user, res.data.token);
        toast.success('Account created!');
        navigate('/pricing');
      }
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      const detail = err.response?.data?.detail;
      if (detail?.code === 'email_not_verified') {
        setPendingEmail(detail.email);
        toast.error(detail.message);
        return;
      }
      const errorMessage = typeof detail === 'string' ? detail : 'Authentication failed. Please check your credentials.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: 'demo@frontdeskpro.com',
        password: 'demo123'
      });
      
      login(res.data.user, res.data.token);
      toast.success('Welcome to the demo!');
      navigate(`/business/${res.data.user.business_id}`);
    } catch (err) {
      toast.error('Demo login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Theme Toggle - Top Right */}
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center shadow-lg shadow-primary/25">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-foreground">
              Front<span className="text-primary">DeskPro</span>
            </span>
          </Link>
        </div>

        <Card className="card-modern p-6">
          {pendingEmail ? <CheckInbox email={pendingEmail} onBack={() => setPendingEmail(null)} /> : (<>
          {/* Tabs */}
          <div className="flex mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-center font-medium transition-colors ${
                isLogin 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground border-b border-border'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-center font-medium transition-colors ${
                !isLogin 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground border-b border-border'
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-name"
                    value={formData.name}
                    onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="John Doe"
                    className="pl-10 bg-muted border-border"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="input-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                  placeholder="you@example.com"
                  className="pl-10 bg-muted border-border"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="input-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(f => ({ ...f, password: e.target.value }))}
                  placeholder="Enter your password"
                  className="pl-10 pr-10 bg-muted border-border"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {isLogin && (
                <div className="mt-1.5 text-right">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <label className="text-sm text-muted-foreground mb-1.5 block">Phone (optional)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+1 555-123-4567"
                    className="pl-10 bg-muted border-border"
                  />
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              data-testid="submit-btn"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Demo Account */}
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground text-center mb-3">
              Want to explore first?
            </p>
            <Button
              data-testid="demo-login-btn"
              variant="outline"
              onClick={handleDemoLogin}
              disabled={loading}
              className="w-full"
            >
              Try Demo Account
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              demo@frontdeskpro.com / demo123
            </p>
          </div>
          </>)}
        </Card>

        {/* Back to home */}
        <p className="text-center mt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to Home</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
